/**
 * The per-count thread. One thread per count, both orgs on the same screen.
 *
 * Opening it collapses the SESSION LIST, never the variance table: when you
 * are discussing a count you need the numbers, not the other counts. While it
 * is open the variance table grows a leading checkbox column, so a reply can
 * point at specific rows (see `SkuReference` for what the reader gets).
 *
 * Bubble visuals follow `features/order-messages/OrderChatPanel.tsx`; the
 * transport is the stocktake thread instead of the order thread.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Send, Settings2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { ClientOrgBadge } from '@/components/ClientOrgBadge';
import { useDateFnsLocale } from '@/lib/format-dates';
import { cn } from '@/lib/utils';
import type { StocktakeMessage, StocktakeProposalLineRow } from '@threadheads/shared';
import { formatDateTime } from '../lib/format';
import { MessageBody } from './MessageBody';
import { SkuReference } from './SkuReference';

const COPIED_LABEL_MS = 1600;

interface StocktakeMessagesProps {
  counterpartyName: string;
  messages: StocktakeMessage[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  linesBySku: ReadonlyMap<string, StocktakeProposalLineRow>;
  attachedSkus: string[];
  onClearAttachments: () => void;
  openReferenceId: string | null;
  onToggleReference: (messageId: string, skus: string[]) => void;
  focusedSku: string | null;
  onJumpToSku: (sku: string) => void;
  onSend: (body: string) => void;
  isSending: boolean;
  pingText: string;
  /**
   * Omitted when the caller may not change the notify config (the backend
   * gates it on org-settings access, so rendering the gear for everyone else
   * is a guaranteed 403 dressed up as a button).
   */
  onOpenNotifySettings?: (() => void) | undefined;
  /**
   * False for read-only roles (Viewer): posting is InventoryWrite server-side.
   * The thread stays readable; the composer is replaced with a quiet note.
   */
  canPost: boolean;
  onClose: () => void;
}

export function StocktakeMessages({
  counterpartyName,
  messages,
  isLoading,
  isError,
  onRetry,
  linesBySku,
  attachedSkus,
  onClearAttachments,
  openReferenceId,
  onToggleReference,
  focusedSku,
  onJumpToSku,
  onSend,
  isSending,
  pingText,
  onOpenNotifySettings,
  canPost,
  onClose,
}: StocktakeMessagesProps) {
  const { t } = useTranslation('inventory');
  const dateLocale = useDateFnsLocale();
  const [draft, setDraft] = useState('');
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<number>();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => () => window.clearTimeout(copyTimer.current), []);

  // Pin to the newest message whenever the count changes.
  useEffect(() => {
    const element = scrollRef.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [messages.length]);

  const copyLink = () => {
    void navigator.clipboard?.writeText(pingText);
    setCopied(true);
    window.clearTimeout(copyTimer.current);
    copyTimer.current = window.setTimeout(() => setCopied(false), COPIED_LABEL_MS);
  };

  const send = () => {
    const body = draft.trim();
    if (!body || isSending) return;
    onSend(body);
    setDraft('');
  };

  const handleComposerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      send();
    }
  };

  return (
    <section
      aria-label={t('stocktakes.messages.paneAria')}
      // No edge of its own: side by side the resize handle draws the divider,
      // stacked the wrapper draws a top border. A border here would double it.
      className="flex min-h-0 w-full flex-col overflow-hidden bg-card"
    >
      <header className="flex shrink-0 items-center gap-2 border-b px-3 py-2.5">
        <ClientOrgBadge
          orgName={counterpartyName}
          srLabel={t('stocktakes.badge.threadAria', { org: counterpartyName })}
        />
        <b className="text-[12.5px] font-semibold">{t('stocktakes.messages.title')}</b>
        <div className="flex-1" />
        {onOpenNotifySettings ? (
          <Button
            variant="outline"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={onOpenNotifySettings}
            aria-label={t('stocktakes.messages.settingsAria')}
          >
            <Settings2 className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        ) : null}
        <Button
          variant="outline"
          size="sm"
          className="h-6 gap-1 px-2 text-[11px]"
          onClick={onClose}
        >
          <X className="h-3 w-3" aria-hidden="true" />
          {t('stocktakes.messages.close')}
        </Button>
      </header>

      <div ref={scrollRef} className="grid min-h-0 flex-1 content-start gap-2.5 overflow-y-auto p-3">
        {isLoading ? (
          <>
            <Skeleton className="h-14 w-4/5 rounded-lg" />
            <Skeleton className="h-10 w-3/5 justify-self-end rounded-lg" />
            <Skeleton className="h-14 w-4/5 rounded-lg" />
          </>
        ) : isError ? (
          <div className="flex flex-col items-center gap-2.5 py-8 text-center">
            <p className="text-sm text-muted-foreground">{t('stocktakes.messages.loadFailed')}</p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              {t('stocktakes.messages.retry')}
            </Button>
          </div>
        ) : messages.length === 0 ? (
          <p className="px-2 py-8 text-center text-sm text-muted-foreground">
            {t('stocktakes.messages.empty', { org: counterpartyName })}
          </p>
        ) : (
          messages.map((message) => {
            if (message.side === 'system') {
              return (
                <p
                  key={message.id}
                  className="justify-self-stretch py-0.5 text-center text-[11px] text-muted-foreground"
                >
                  {message.body}
                </p>
              );
            }
            const own = message.side === 'you';
            return (
              <div
                key={message.id}
                className={cn(
                  'max-w-[92%] rounded-lg px-2.5 py-2 text-xs leading-relaxed',
                  own
                    ? 'justify-self-end rounded-tr-sm bg-threadheads-purple/10 ring-1 ring-threadheads-purple/25'
                    : 'justify-self-start rounded-tl-sm border bg-muted/60',
                )}
              >
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {message.authorName ?? counterpartyName}
                  {message.createdAt
                    ? `, ${formatDateTime(message.createdAt, dateLocale)}`
                    : ''}
                </span>
                <MessageBody body={message.body} />
                <SkuReference
                  skus={message.skus}
                  linesBySku={linesBySku}
                  open={openReferenceId === message.id}
                  onToggle={() => onToggleReference(message.id, message.skus)}
                  focusedSku={focusedSku}
                  onJump={onJumpToSku}
                />
              </div>
            );
          })
        )}
      </div>

      {!canPost ? (
        <p className="shrink-0 border-t px-3 py-2.5 text-[11.5px] text-muted-foreground">
          {t('stocktakes.messages.readOnly')}
        </p>
      ) : (
        <div className="grid max-h-[46%] shrink-0 gap-2 overflow-y-auto border-t px-3 py-2.5">
          {attachedSkus.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 text-[11.5px] text-muted-foreground">
              <span className="inline-flex items-center gap-1 rounded-full bg-threadheads-purple/10 px-2 py-0.5 text-[11px] font-semibold text-threadheads-purple ring-1 ring-threadheads-purple/30">
                {t('stocktakes.messages.attached', { count: attachedSkus.length })}
              </span>
              <span className="min-w-0 truncate">
                {attachedSkus.slice(0, 3).join(', ')}
                {attachedSkus.length > 3 ? (
                  <>
                    {' '}
                    {t('stocktakes.messages.andMore', { count: attachedSkus.length - 3 })}
                  </>
                ) : null}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-[11px]"
                onClick={onClearAttachments}
              >
                {t('stocktakes.messages.clearAttachments')}
              </Button>
            </div>
          ) : null}
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleComposerKeyDown}
            placeholder={t('stocktakes.messages.placeholder', { org: counterpartyName })}
            aria-label={t('stocktakes.messages.composerAria', { org: counterpartyName })}
            className="min-h-[66px] resize-y bg-background text-xs"
          />
          <div className="flex items-center gap-1.5">
            <div className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={copyLink}
              aria-live="polite"
            >
              {copied ? t('stocktakes.composer.linkCopied') : t('stocktakes.composer.copyLink')}
            </Button>
            <Button
              size="sm"
              className="h-7 gap-1.5 bg-threadheads-purple px-2.5 text-xs text-white hover:bg-threadheads-purple/90"
              onClick={send}
              disabled={isSending || draft.trim().length === 0}
            >
              {isSending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {t('stocktakes.messages.send')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
